import React, {useEffect, useState} from 'react';
import ReduxToastr from 'react-redux-toastr';
import {useDispatch, useSelector} from 'react-redux';
import { Sidebar, Sticky } from 'semantic-ui-react';

import {MixpanelProvider, useMixpanel} from 'react-mixpanel-browser';

import { selectMenuVisible } from './features/ui/selectors';
import { closeMenu, closeSearch } from './features/ui/actions';
import NavTopBar, { MobileSidebar } from './components/NavTopBar';
import NavBottomBar from './components/NavBottomBar';
import SideMenu from './views/SideMenu';

import './App.css';
import {MIXPANEL_API_KEY} from "./config/secrets";
import { selectSearchVisible } from './features/ui/selectors';

import {useNavigate} from "react-router-dom";
import { selectToken, selectUser, selectIsLoggedIn } from "./features/auth/selectors";
import { selectLocation } from "./features/geolocation/selectors";
import isArray from "lodash/isArray";
import size from "lodash/size";
import { selectSearchToContext } from "./features/search/selectors";
import { initGeoLocation } from './features/geolocation';
import { setSearchContext } from './features/search/actions';


const MIXPANEL_TOKEN = MIXPANEL_API_KEY;

const MIXPANEL_CONFIG = {
  track_pageview: true,
}

export const Base64= {_keyStr:"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",encode:function(e){var t="";var n,r,i,s,o,u,a;var f=0;e=Base64._utf8_encode(e);while(f<e.length){n=e.charCodeAt(f++);r=e.charCodeAt(f++);i=e.charCodeAt(f++);s=n>>2;o=(n&3)<<4|r>>4;u=(r&15)<<2|i>>6;a=i&63;if(isNaN(r)){u=a=64}else if(isNaN(i)){a=64}t=t+this._keyStr.charAt(s)+this._keyStr.charAt(o)+this._keyStr.charAt(u)+this._keyStr.charAt(a)}return t},decode:function(e){var t="";var n,r,i;var s,o,u,a;var f=0;e=e.replace(/[^A-Za-z0-9\+\/\=]/g,"");while(f<e.length){s=this._keyStr.indexOf(e.charAt(f++));o=this._keyStr.indexOf(e.charAt(f++));u=this._keyStr.indexOf(e.charAt(f++));a=this._keyStr.indexOf(e.charAt(f++));n=s<<2|o>>4;r=(o&15)<<4|u>>2;i=(u&3)<<6|a;t=t+String.fromCharCode(n);if(u!=64){t=t+String.fromCharCode(r)}if(a!=64){t=t+String.fromCharCode(i)}}t=Base64._utf8_decode(t);return t},_utf8_encode:function(e){e=e.replace(/\r\n/g,"\n");var t="";for(var n=0;n<e.length;n++){var r=e.charCodeAt(n);if(r<128){t+=String.fromCharCode(r)}else if(r>127&&r<2048){t+=String.fromCharCode(r>>6|192);t+=String.fromCharCode(r&63|128)}else{t+=String.fromCharCode(r>>12|224);t+=String.fromCharCode(r>>6&63|128);t+=String.fromCharCode(r&63|128)}}return t},_utf8_decode:function(e){var t="";var n=0;var r=c1=c2=0;while(n<e.length){r=e.charCodeAt(n);if(r<128){t+=String.fromCharCode(r);n++}else if(r>191&&r<224){c2=e.charCodeAt(n+1);t+=String.fromCharCode((r&31)<<6|c2&63);n+=2}else{c2=e.charCodeAt(n+1);c3=e.charCodeAt(n+2);t+=String.fromCharCode((r&15)<<12|(c2&63)<<6|c3&63);n+=3}}return t}}


function App (props) {

  const [ sidebarVisible , setSidebarVisible ] = useState(null);
  const [ activeSubmenu, setActiveSubmenu ] = useState(null);

  const router = useSelector((state) => state.router);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchContext = useSelector(selectSearchToContext);
  const userData = useSelector(selectUser);
  const userLocation = useSelector(selectLocation);
  const mixpanel = useMixpanel();

  // State для поиска в мобильном меню
  const [searchState, setSearchState] = useState({ search: '', context: searchContext });

  const defaultProps = {
    navigate,
    dispatch,
    searchVisible: useSelector(selectSearchVisible),
    menuVisible: useSelector(selectMenuVisible),
    userData,
    userLocation,
    isLoggedIn: useSelector(selectIsLoggedIn),
    token: useSelector(selectToken),
    mixpanel,
    routeAction: router.action,
    location: router.location,
    previousLocations: router.previousLocations,
    isPageReloaded: router.action === 'POP' && isArray(router.previousLocations) && size(router.previousLocations) === 1,
  }

  const {children} = props;
  const {menuVisible, searchVisible} = defaultProps;

  useEffect(() => {
    dispatch(initGeoLocation());
  }, []);

  function hideBars () {
    if (menuVisible)
      dispatch(closeMenu());

    if (searchVisible)
      dispatch(closeSearch());

    if (sidebarVisible)
      setSidebarVisible(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSidebarVisible(false);
    dispatch(closeSearch());
    dispatch(setSearchContext(searchState.context));
    navigate(`/search?q=${encodeURIComponent(searchState.search)}`, {state: {doQuery: true}});
    if (mixpanel)
      mixpanel.track('Search Confirm', { ...userLocation, ...userData, ...searchState });
  }

  function setSearch(e) {
    setSearchState({ ...searchState, search: e.target.value });
  }

  const handleCategoryClick = (e, categoryName) => {
    e.stopPropagation();
    setActiveSubmenu(activeSubmenu === categoryName ? null : categoryName);
  };

  const handleSimpleCategoryClick = () => {
    setActiveSubmenu(null);
  };

  return (
      <MixpanelProvider config={MIXPANEL_CONFIG} token={MIXPANEL_TOKEN}>
        <div>
          <ReduxToastr
              timeOut={4000}
              newestOnTop
              preventDuplicates
              position="top-center"
              transitionIn="fadeIn"
              transitionOut="fadeOut"
              progressBar
              closeOnToastrClick
              getState={(state) => state.toastr}
          />
          <NavTopBar {...defaultProps} sidebarVisible={sidebarVisible} setSidebarVisible={setSidebarVisible} />
          <Sidebar.Pushable style={{ transform: 'none' }}>
            <MobileSidebar
                sidebarVisible={sidebarVisible}
                setSidebarVisible={setSidebarVisible}
                activeSubmenu={activeSubmenu}
                setActiveSubmenu={setActiveSubmenu}
                handleCategoryClick={handleCategoryClick}
                handleSimpleCategoryClick={handleSimpleCategoryClick}
                searchState={searchState}
                setSearch={setSearch}
                handleSubmit={handleSubmit}
                navigate={navigate}
            />
            <Sticky>
              <SideMenu {...defaultProps}/>
            </Sticky>
            <Sidebar.Pusher
                dimmed={menuVisible || searchVisible || sidebarVisible}
                onClick={hideBars}
                style={{ minHeight: '100vh' }}
            >
              <main className="main-content">
                {children}
              </main>
            </Sidebar.Pusher>
          </Sidebar.Pushable>
          <NavBottomBar {...defaultProps}/>
        </div>
      </MixpanelProvider>
  );
}

export default App;
